import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router';
import { Observable, ReplaySubject, filter, mergeMap, of } from 'rxjs';
import { map, take } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class ReloadHandlerService {
  // router.events is a hot stream — the initial NavigationEnd fires once,
  // early in bootstrap, and is gone for any subscriber that arrives later
  // (e.g. a consumer gated behind an async wait). Listening starts here, in
  // the constructor, so it's captured regardless of when reloadHandler() is
  // actually called; the ReplaySubject then replays that result to every
  // caller, early or late.
  private readonly resultSub = new ReplaySubject<Params | null>(1);

  constructor(private router: Router, private route: ActivatedRoute) {
    this.router.events
      .pipe(
        filter((rs) => rs instanceof NavigationEnd),
        take(1),
        mergeMap((event) => {
          const performance = window.performance;

          if (!performance?.getEntriesByType) return of(null);
          const navigation = performance.getEntriesByType('navigation');

          if (!navigation?.length) return of(null);

          if (
            (event as NavigationEnd).id === 1 &&
            (navigation[0] as PerformanceNavigationTiming).type === 'reload'
          ) {
            const params = { ...this.route.snapshot.queryParams };

            delete params['publicId'];
            delete params['companyId'];
            this.router.navigate([], { queryParams: params }).then();

            return of(null);
          }

          return this.route.queryParams.pipe(
            take(1),
            map((queryParams) => {
              if (!Object.keys(queryParams).length) return null;

              return queryParams;
            })
          );
        })
      )
      .subscribe((result) => this.resultSub.next(result));
  }

  reloadHandler(): Observable<Params | null> {
    return this.resultSub.asObservable();
  }
}
